import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

// Dedicated non-paginated read model for the Prices page: the family's FULL
// variant list plus every supplier deal quote. The paginated CRUD list caps
// limit at 100 and can't serve the matrix shape.
const endpoint = 'api/v1/cms/exclusive-deals/family-matrix';
export const getFamilyPriceMatrixEndpointIdentifier = endpoint;

export interface FamilyMatrixAttribute {
  attributeType: string;
  attributeValue: string;
  isIdentity: boolean;
}

export interface FamilyMatrixVariant {
  id: number;
  familyId: number;
  // The family's universalName — variants carry no own name.
  name: string | null;
  brand: string | null;
  origin: string | null;
  state: string | null;
  spec: string | null;
  baseUnit: string;
  netContent: number | null;
  itemUnit: string | null;
  packUnit: string | null;
  packSize: number | null;
  isActive: boolean;
  attributes: FamilyMatrixAttribute[];
}

export interface FamilyMatrixDeal {
  id: number;
  masterProductId: number;
  supplierId: number;
  supplierName: string | null;
  dealPrice: number;
  // Ladder rung the price is agreed against (stored on the deal since
  // 2026-08-12); null only on rows the BE couldn't resolve a rung for.
  dealUnit: 'PACK' | 'ITEM' | 'BASE' | null;
  // dealPrice restated per base unit (BE-derived); null when a ladder hop
  // (packSize/netContent) is unknown.
  baseUnitPrice: number | null;
  isActive: boolean;
  lastModifiedAt: string | null;
}

export interface FamilyPriceMatrixResponse {
  familyId: number;
  variants: FamilyMatrixVariant[];
  deals: FamilyMatrixDeal[];
}

interface ResponseData {
  data: FamilyPriceMatrixResponse;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, number | undefined];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetFamilyPriceMatrixApi(familyId: number | undefined, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, { params: { familyId } });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, familyId], queryFn, {
    enabled: isLoggedIn && !!familyId,
    ...options,
  });
}
